import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import parse from 'html-react-parser';

// Components

import SwitchCheckbox from '../inputs/SwitchCheckbox';
import Checkbox from '../inputs/Checkbox';
import DatePicker from '../inputs/DatePicker';
import Dropdown from '../inputs/Dropdown';
import DropdownWithTwoLabels from '../inputs/DropdownWithTwoLabels';
import Input from '../inputs/Input';
import InputWithLRBtn from '../inputs/InputWithLRBtn';
import MoneyInput from '../inputs/MoneyInput';
import PhoneNumberInput from '../inputs/PhoneNumberInput';
import RadioButtons from '../inputs/RadioButtons';
import RadioCheckBoxes from '../inputs/RadioCheckBoxes';
import SocialSecurityNumberInput from '../inputs/SocialSecurityNumberInput';
import Textarea from '../inputs/Textarea';
import ValidationErrorMessage from '../../components/ssap/notifications/ValidationErrorMessage';

// ActionCreators

import { changeInputStatus, registerInput, reRegisterInput, removeInput } from '../../actions/ssap/ssapActionCreators';

// Hooks

import { useToggle } from '../../hooks/useToggle';

// Utils

import mergeRecursively from '../../utils/miscellaneous/mergeRecursively';
import { pageNumberAdjuster } from '../../utils/normalize/pageNumberAdjuster';
import { localStorageHyperDashboardTools } from '../../utils/tools/localStorageHyperDashboardTools';

// Constants

import validators from '../../utils/validators/validators';
import { INPUT_TYPES } from '../../constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '../../constants/inputs/validationErrorMessages';
import { useIntlTagFormatter } from '../../utils/miscellaneous/react-intl-utlis';



const UniversalInput = props => {

    const {
        inputData,
        inputData: {
            areAllFieldsMixed = false,
            currentData,
            disabled,
            isInputRequired,
            isInputStandalone = false,
            isValidationErrorMessageVisible = true,
            fields = [],
            name,
            options,
            optionsReplacements,
            placeholder,
            type,
            validationErrorMessage,
            validationErrorValues,
            specificValidation,
            value,
            valuesToMatch,
            validationType,
            isToggleReRegistration = undefined,
            reversedValue = false,
            renderAs = null
        },
        indexInArray,
        inputActions,
        inputActions: {
            onFieldBlur,
            onFieldChange
        } = {},
        errorClasses,
        label = {},
        legend,
        legend: {
            legendText = '',
            legendTextReplacements = {},
            parseLegend = false
        } = {},
    } = props;

    const {
        parentForm: {
            name: formName
        },
        form,
        page
    } = props;

    const {
        isFormSubmitted,
        registeredInputs
    } = form[formName];

    const [ isCharExceptionShown, toggleVisibility ] = useToggle(false);
    const [ notEligibleChars, changeChars ] = useState('');

    const intlTagFormatter = useIntlTagFormatter();

    const resolveValue = value => {
        switch(reversedValue) {
            case true:
                switch(value) {
                    case true:
                        return false;
                    case false:
                        return true;
                    default:
                        return value;
                }
            case false:
                return value;
        }
    };

    const getFields = arr => {
        if(Array.isArray(arr)) {
            return arr.length === 0
                ? ''
                : areAllFieldsMixed
                    ? `_${arr.join('_')}`
                    : `_${arr[arr.length - 1]}`;
        } else {
            return '';
        }
    };

    const getHouseholdMemberIndex = index => {
        switch(true) {
            case typeof index === 'number':
                return index;
            case Array.isArray(index):
                return index.reduce((outputStr, el) => {
                    return `${outputStr}_${getHouseholdMemberIndex(el)}`;
                }, '');
            default:
                return null;
        }
    };

    const prepend = value => value !== null ? `_hhm${value}` : '';

    const createInputID = () => {
        return [
            pageNumberAdjuster(page),
            prepend(getHouseholdMemberIndex(indexInArray)),
            getFields(fields),
            `_${name}`
        ].join('');
    };

    const ID = createInputID();

    const validate = (value, valuesToMatch) => {

        const isValidatorsGeneratorResultsShown = localStorageHyperDashboardTools.getValueByPropName('isValidatorsGeneratorResultsShown', false);

        switch(true) {
            case !validator:
                return true;
            case !isInputRequired && (value === null || value === '') && !specificValidation:
                return true;
            case isInputRequired && (value === null || value === ''):
                return false;
            default:
                return !!(validator && validator.verifier !== undefined && validator.verifier({
                    value,
                    valuesToMatch,
                    isGenShown: isValidatorsGeneratorResultsShown
                }));
                
        }
    };

    const validator = validators.find(v => v.validatorType === (!isInputRequired && specificValidation ? specificValidation.validationType : props.inputData.validationType));

    useEffect(() => { // Mounting and unmounting inputs causes registration and removing
        props.actions.registerInput({
            inputId: ID,
            initialInputValue: resolveValue(value),
            isInputTouched: null,
            inputName: name,
            inputValue:  resolveValue(value),
            isInputRequired: isInputRequired,
            isInputStandalone: isInputStandalone,
            inputIsValid: validate(resolveValue(value), valuesToMatch),
            messages: validator && validator.messages,
            inputValidationType: validationType ? validationType : null,
            validOf() {
                return this.inputIsValid;
            },
            valueOf() {
                return this.inputValue;
            }
        }, formName);
        return () => {
            props.actions.removeInput({ inputId: ID }, formName);
        };
    }, []);

    useEffect(() => {
        if(isToggleReRegistration !== undefined) {
            props.actions.reRegisterInput({
                inputId: ID,
                initialInputValue:  resolveValue(value),
                isInputTouched: null,
                inputName: name,
                inputValue:  resolveValue(value),
                isInputRequired: isInputRequired,
                isInputStandalone: isInputStandalone,
                inputIsValid: validate(resolveValue(value), valuesToMatch),
                messages: validator && validator.messages,
                inputValidationType: validationType ? validationType : null,
                validOf() {
                    return this.inputIsValid;
                },
                valueOf() {
                    return this.inputValue;
                }
            }, formName);
        }
    }, [ isToggleReRegistration ]);

    const watchedValues = () => {
        switch (true) {
            case valuesToMatch === null:
                return valuesToMatch;
            case valuesToMatch instanceof Set:
                return [ ...valuesToMatch ].toString();
            case typeof valuesToMatch === 'object':
                return Object.values(valuesToMatch).toString();
            default:
                return valuesToMatch;
        }
    };

    useEffect(() => { // Here we watch on any change in registered input or valuesToMatch and validate it
        const isValid = validate(resolveValue(value), valuesToMatch);

        props.actions.changeInputStatus({
            inputId: ID,
            inputValue: resolveValue(value),
            inputIsValid: isValid
        }, formName);

    }, [ resolveValue(value), watchedValues() ]);

    useEffect(() => {
        isFormSubmitted !== null && validate(resolveValue(value), valuesToMatch);
    }, [isFormSubmitted]);

    const getNewData = updatedValue => {
        const resolvedName = name[0] === '_' ? name.slice(1) : name;
        const injectedProp = { [resolvedName]: updatedValue };
        return mergeRecursively(currentData, injectedProp, [], fields, updatedValue, resolvedName);
    };

    const handleFieldChange = (updatedValue, text = undefined) => {
        if(disabled) return null;

        onFieldChange && onFieldChange(getNewData(resolveValue(updatedValue)), indexInArray, fields, [name, resolveValue(updatedValue), disabled, text]);
    };

    const handleFieldBlur = updatedValue => {
        if(disabled) return null;

        if(/^[\x20-\x7e]*$/.test(updatedValue)) {
            isCharExceptionShown && toggleVisibility(false);
            isCharExceptionShown && changeChars('');
            onFieldBlur && onFieldBlur(getNewData(resolveValue(updatedValue)), indexInArray, fields, [name, resolveValue(updatedValue), disabled]);
        } else {
            if(type !== INPUT_TYPES.TEXTAREA) {
                const checkIfLineBreak = char => char.charCodeAt(0) === 10 ? 'Line break' : char;
                toggleVisibility(true);
                changeChars([
                    ...updatedValue].reduce((res, char) => !/^[\x20-\x7e]*$/.test(char) ? [ ...res, checkIfLineBreak(char) ] : res, []).join(', '));
                handleFieldChange('');
            }
        }
    };

    const getInput = () => {

        const wrapStringWithLessGreater = str => `\u003C${str}\u003E`;

        const checkIntlId = (id, pattern) => {
            if(typeof id === 'string' && id.includes(pattern)) {
                return true;
            } else {
                localStorageHyperDashboardTools
                    .getValueByPropName('isReactIntlWarningsShown', false) && id && console.log(
                    `Property ${wrapStringWithLessGreater((p => p[p.length - 1])(pattern.split('.')))} in input with name ${wrapStringWithLessGreater(name)} does not have expected pattern id ${wrapStringWithLessGreater(pattern)}`
                );
                return false;
            }
        };

        const parseString = string => {
            return typeof string === 'string' && parseLegend
                ? parse(string)
                : string;
        };

        const processId = (id, pattern, replacements = {}) => {
            return checkIntlId(id, pattern) ? intlTagFormatter(id, replacements) : id;
        };

        const intlOptionsExceptionsList = [
            'countyCode',
            'state',
            'foreignPassportCountryOfIssuance',
            'tribeName',
            'fosterCareState',
            'ageWhenLeftFosterCare'
        ];
        
        const processOptions = (options, exceptions) => {
            
            const isInputOptionsUnderExceptions = exceptions.some(exception => exception === name);
            
            return options ? 
                { 
                    options: options.map(option => {

                        const processedId = processId( option.text, 'input.option', optionsReplacements);

                        const processRecursively = (value, key) => {
                            if (!React.isValidElement(value) && typeof value === 'string') {
                                return parse(value);
                            } else if (React.isValidElement(value)) {
                                const { props: { children: childerValue = [] } = {} } = value;

                                return React.cloneElement(
                                    value,
                                    { key },
                                    React.isValidElement(childerValue)
                                        ? processRecursively(childerValue)
                                        : Array.isArray(childerValue)
                                            ? childerValue.map((ch, idx) => processRecursively(ch, idx))
                                            : childerValue,
                                );
                            } else {
                                return value;
                            }
                        };

                        return  isInputOptionsUnderExceptions && option.value !== 'default' ?
                            option : // TODO: Come up with how to create optionId for exceptions and 'default' to support data-testid
                            {
                                ...option,
                                text: processRecursively(processedId),
                                optionId: typeof option.text === 'string'
                                    ? option.text.split('.').slice(-1).pop()
                                    : 'no_optionId'
                            };
                    }) 
                } :
                {};
        };

        const processLabelProps = () => {
            const isTwoLabelComponent = !!label.leftLabel && !!label.rightLabel;

            const getLabelProps = label => {
                const { text, replacements } = label;
                return {
                    ...label,
                    text: processId(text, 'input.label', replacements)
                };
            };

            if(isTwoLabelComponent) {
                const {
                    leftLabel,
                    rightLabel,
                } = label;

                return {
                    ...label,
                    leftLabel: getLabelProps(leftLabel),
                    rightLabel: getLabelProps(rightLabel)
                };
            } else {
                return getLabelProps({ ...label, replacements: { ...label.replacements, ...label.labelTextReplacements } });
            }
        };

        const injectedProps = {
            ...props,
            inputData:{
                ...inputData,
                type: type.toLowerCase(),
                id: ID,
                placeholder: processId(placeholder, 'input.placeholder'),
                ...processOptions(options, intlOptionsExceptionsList)
            },
            inputActions: {
                ...inputActions,
                handleFieldChange: handleFieldChange, // Injecting handleFieldChange()
                handleFieldBlur: handleFieldBlur      // Injecting handleFieldBlur()
            },
            label: processLabelProps(),
            legend: {
                ...legend,
                legendText: processId(legendText, 'input.legend', legendTextReplacements)
            }
        };



        switch (type) {
            case INPUT_TYPES.CHECKBOX:
                return <Checkbox { ...injectedProps }/>;

            case INPUT_TYPES.SWITCH_CHECKBOX:
                return <SwitchCheckbox {...injectedProps} />;

            case INPUT_TYPES.DATE_PICKER:
                return <DatePicker { ...injectedProps } />;

            case INPUT_TYPES.DROPDOWN:
                return <Dropdown { ...injectedProps } />;

            case INPUT_TYPES.DROPDOWN_WITH_TWO_LABELS:
                return <DropdownWithTwoLabels { ...injectedProps } />;

            case INPUT_TYPES.DUMMY:
                return renderAs;

            case INPUT_TYPES.REGULAR_INPUT:
                return <Input { ...injectedProps } />;

            case INPUT_TYPES.INPUT_LR_BUTTONS:
                return <InputWithLRBtn { ...injectedProps } />;

            case INPUT_TYPES.MONEY_INPUT:
                return <MoneyInput { ...injectedProps } />;

            case INPUT_TYPES.PHONE_NUMBER_INPUT:
                return <PhoneNumberInput { ...injectedProps }/>;

            case INPUT_TYPES.RADIO_BUTTONS:
                return <RadioButtons { ...injectedProps } />;

            case INPUT_TYPES.RADIO_CHECKBOXES:
                return <RadioCheckBoxes { ...injectedProps }/>;

            case INPUT_TYPES.SSN_INPUT:
                return <SocialSecurityNumberInput { ...injectedProps }/>;

            case INPUT_TYPES.TEXTAREA:
                return <Textarea { ...injectedProps } />;

            default:
                return null;
        }
    };
    
    const getValidationErrorMessage = () => {

        switch(true) {

            case isCharExceptionShown:
                return (
                    <ValidationErrorMessage
                        isVisible = { isValidationErrorMessageVisible }
                        messageText = { VALIDATION_ERROR_MESSAGES.INVALID_CHARS }
                        errorClasses = { errorClasses }
                        messageTextReplacements = { { notEligibleChars } }
                    />
                );

            case isFormSubmitted && !isInputValid:
                return (
                    <ValidationErrorMessage
                        isVisible = { isValidationErrorMessageVisible }
                        messageText = { message }
                        errorClasses = { errorClasses }
                        messageTextReplacements = { validationErrorValues }
                    />
                );

            default: 
                return null;
        }
    };

    const inputFromForm = registeredInputs.length > 0 && registeredInputs.find(input => input.inputId === ID);

    const isInputValid = inputFromForm && inputFromForm.inputIsValid;

    const message = validationErrorMessage ? validationErrorMessage : inputFromForm && inputFromForm.messages && inputFromForm.messages[0];

    return (
        <Fragment>
            {
                getValidationErrorMessage()
            }
            {
                getInput()
            }
        </Fragment>
    );
};

const mapStateToProps = state => ({
    form: state.ssap.form,
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeInputStatus,
        registerInput,
        reRegisterInput,
        removeInput
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(UniversalInput);
